import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import {
  useGetExperienceOfferRelatedLocations,
  useUpdateExperienceOfferRelatedLocation,
} from '~/hooks/apis/experiences/useExperienceOfferRelatedLocations';

import { ExperienceOfferRelatedLocation } from '~/services/ExperiencesService';

import getErrorMessage from '~/utils/getErrorMessage';

import RelatedLocationsMap from './RelatedLocationsMap';

interface Props {
  offerId: string;
  offerLocation?: { latitude: number; longitude: number };
  readOnly?: boolean;
}

export default function ExperienceCurationRelatedLocations({ offerId, offerLocation, readOnly = false }: Props) {
  const locationsQuery = useGetExperienceOfferRelatedLocations(offerId);
  const updateLocation = useUpdateExperienceOfferRelatedLocation(offerId);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [locationToUpdate, setLocationToUpdate] = useState<ExperienceOfferRelatedLocation | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const locations = locationsQuery.data ?? [];
  const excludedCount = locations.filter((location) => location.excluded).length;

  function requestUpdate(location: ExperienceOfferRelatedLocation) {
    updateLocation.reset();
    setSuccessMessage(null);
    setLocationToUpdate(location);
  }

  function closeDialog() {
    if (!updateLocation.isPending) setLocationToUpdate(null);
  }

  async function confirmUpdate() {
    if (!locationToUpdate || readOnly) return;

    const excluded = !locationToUpdate.excluded;
    try {
      await updateLocation.mutateAsync({ locationId: locationToUpdate.id, excluded });
      setLocationToUpdate(null);
      setSuccessMessage(excluded ? 'Related location excluded.' : 'Related location restored.');
    } catch {
      // Keep the dialog open and show the mutation error so the curator can retry.
    }
  }

  return (
    <Stack spacing={2}>
      <PageSubheader title="Related locations">
        <Button
          onClick={() => locationsQuery.refetch()}
          disabled={locationsQuery.isFetching || updateLocation.isPending}
        >
          Refresh locations
        </Button>
      </PageSubheader>
      <Typography variant="body2" color="text.secondary">
        Review these points and exclude any that do not belong to this experience. Excluded points remain visible here
        and can be restored. Changes save immediately.
      </Typography>
      {readOnly && (
        <Alert severity="info">Open the Experience content tab to exclude or restore related locations.</Alert>
      )}

      {locationsQuery.isLoading && (
        <Stack direction="row" spacing={1} alignItems="center" role="status">
          <CircularProgress size={24} />
          <Typography>Loading related locations…</Typography>
        </Stack>
      )}
      {locationsQuery.isError && (
        <Alert severity="error" action={<Button onClick={() => locationsQuery.refetch()}>Retry</Button>}>
          {getErrorMessage(locationsQuery.error, 'Unable to load related locations.')}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {locationsQuery.isSuccess && locations.length === 0 && (
        <Alert severity="info">No related locations have been saved for this experience.</Alert>
      )}

      {locations.length > 0 && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`${locations.length - excludedCount} included`} color="primary" variant="outlined" />
            <Chip label={`${excludedCount} excluded`} variant="outlined" />
            {offerLocation && <Chip label="O: Offer location" variant="outlined" />}
          </Stack>
          <RelatedLocationsMap
            locations={locations}
            offerLocation={offerLocation}
            selectedLocationId={selectedLocationId}
            onSelectLocation={setSelectedLocationId}
          />
          <TableContainer component={Box}>
            <Table size="small" aria-label="Related locations">
              <TableHead>
                <TableRow>
                  <TableCell>Point</TableCell>
                  <TableCell>Coordinates</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.map((location, index) => (
                  <TableRow key={location.id} selected={selectedLocationId === location.id}>
                    <TableCell>
                      <Button
                        aria-label={`Show point ${index + 1} on map`}
                        onClick={() => setSelectedLocationId(location.id)}
                      >
                        Point {index + 1}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Link
                        href={`https://www.google.com/maps/search/?api=1&query=${location.latitude},${location.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {location.latitude}, {location.longitude}
                      </Link>
                    </TableCell>
                    <TableCell>{location.lookupProvider}</TableCell>
                    <TableCell>
                      <Chip
                        label={location.excluded ? 'Excluded' : 'Included'}
                        color={location.excluded ? 'default' : 'primary'}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        aria-label={`${location.excluded ? 'Restore' : 'Exclude'} point ${index + 1}`}
                        onClick={() => requestUpdate(location)}
                        disabled={updateLocation.isPending || readOnly}
                      >
                        {location.excluded ? 'Restore' : 'Exclude'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={!!locationToUpdate} onClose={closeDialog} aria-labelledby="related-location-dialog-title">
        <DialogTitle id="related-location-dialog-title">
          {locationToUpdate?.excluded ? 'Restore related location?' : 'Exclude related location?'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              {locationToUpdate?.excluded
                ? 'This point will be included in the experience’s related locations again.'
                : 'This point will be excluded from the experience’s related locations. You can restore it later.'}
            </Typography>
            {locationToUpdate && (
              <Typography variant="body2">
                {locationToUpdate.latitude}, {locationToUpdate.longitude}
              </Typography>
            )}
            {updateLocation.isError && (
              <Alert severity="error">{getErrorMessage(updateLocation.error, 'Unable to update this location.')}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={updateLocation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmUpdate} disabled={updateLocation.isPending || readOnly}>
            {updateLocation.isPending
              ? 'Saving…'
              : locationToUpdate?.excluded
                ? 'Restore location'
                : 'Exclude location'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
